import { useState } from 'react';

import { css, cx } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';
import { patientFormattedName } from '@/models/UserModel';
import { Queries } from '@/queries/Queries';
import { PatientRenewal } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx';
import { toDateTime } from '@/utils/dateUtils';

import { Button } from '../../atoms/Button';
import { InformationPaper } from '../../atoms/InformationPaper';

type DoctorRenewNudgeProps = {
  patientId: string;
};

type DoctorRenewNudgeBannerProps = {
  patientId: string;
  expiresIn: number;
};

const DoctorRenewNudgeBannerExpired = ({
  patientId,
  expiresIn,
}: DoctorRenewNudgeBannerProps) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const refuseToRenew = Queries.practitioner.useRefuseToRenew();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const patient = Queries.practitioner.usePatient(patientId);

  return (
    <>
      <InformationPaper
        isSquare
        variant="outlined"
        className={cx(styles.paper, styles.expiredColor)}
      >
        <div>
          <Typography variant="body">
            <Trans
              i18nKey="patientNavigation.renewalNudge.expired"
              values={{
                days: expiresIn,
                patientName: patientFormattedName(
                  patient.familyName,
                  patient.givenName,
                ),
              }}
              components={[<Typography variant="subtitle" key="1" />]}
            />
          </Typography>
        </div>
        <div className={styles.ctas}>
          <Button
            variant="contained"
            onClick={() => setIsModalOpen(true)}
            id="renew-from-renew-nudge"
          >
            {t('patientNavigation.renewalNudge.ctas.continue')}
          </Button>
          <Button
            onClick={() => refuseToRenew.mutate(patientId)}
            variant="contained"
            pending={refuseToRenew.isPending}
            id="refused-to-renew"
          >
            {t('patientNavigation.renewalNudge.ctas.stopNow', {
              days: expiresIn,
            })}
          </Button>
        </div>
      </InformationPaper>
      <PatientRenewal
        patientId={patientId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={() => setIsModalOpen(false)}
      />
    </>
  );
};

const DoctorRenewNudgeBannerExpiresSoon = ({
  patientId,
  expiresIn,
}: DoctorRenewNudgeBannerProps) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const refuseToRenew = Queries.practitioner.useRefuseToRenew();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const patient = Queries.practitioner.usePatient(patientId);

  return (
    <>
      <InformationPaper
        isSquare
        variant="outlined"
        className={cx(styles.paper, styles.expiresSoonColor)}
      >
        <div>
          <Typography variant="body">
            <Trans
              i18nKey="patientNavigation.renewalNudge.expiresSoon"
              values={{
                days: expiresIn,
                patientName: patientFormattedName(
                  patient.familyName,
                  patient.givenName,
                ),
              }}
              components={[<Typography variant="subtitle" key="1" />]}
            />
          </Typography>
        </div>
        <div className={styles.ctas}>
          <Button
            variant="contained"
            onClick={() => setIsModalOpen(true)}
            id="renew-from-renew-nudge"
          >
            {t('patientNavigation.renewalNudge.ctas.continue')}
          </Button>
          <Button
            onClick={() => refuseToRenew.mutate(patientId)}
            variant="contained"
          >
            {t('patientNavigation.renewalNudge.ctas.stopLater', {
              days: expiresIn,
            })}
          </Button>
        </div>
      </InformationPaper>
      <PatientRenewal
        patientId={patientId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={() => setIsModalOpen(false)}
      />
    </>
  );
};

export const DoctorRenewNudge = ({ patientId }: DoctorRenewNudgeProps) => {
  const nudge = Queries.practitioner.useShouldNudgeRenewal(patientId);

  if (nudge.should_nudge && nudge.last_cp_end) {
    const lastCarePlanExpire = toDateTime(nudge.last_cp_end);
    const now = DateTime.now();

    const diff = lastCarePlanExpire.diff(now, ['days']);
    const numberOfDays = Math.round(diff.days);

    if (numberOfDays <= 0) {
      return (
        <DoctorRenewNudgeBannerExpired
          patientId={patientId}
          expiresIn={-1 * numberOfDays}
        />
      );
    } else {
      return (
        <DoctorRenewNudgeBannerExpiresSoon
          expiresIn={numberOfDays}
          patientId={patientId}
        />
      );
    }
  }

  return null;
};

const makeStyles = (theme: Theme) => ({
  paper: css`
    display: flex;
    width: 100%;
    padding: ${theme.spacing(8)} ${theme.spacing(12)};
    flex-direction: column;
    flex-wrap: no-wrap;
    gap: ${theme.spacing(6)};
  `,
  expiresSoonColor: css`
    background-color: ${theme.palette.purple.contrastText};
  `,
  expiredColor: css`
    background-color: ${theme.palette.warning.contrastText};
  `,
  ctas: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(8)};
  `,
});
